import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { IFilterConfigurations } from '../../models/filter.model';
import { ProductType } from '../../enums/product.enum';
import { FilterApiService } from '../../services/api/filter-api.service';
import { FilterStateService } from '../../services/state/filter-state.service';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss']
})
export class FilterComponent implements OnInit {

  @Input() productType: ProductType;
  @Input() filterConfigurations: IFilterConfigurations;
  @Input() filterButtonsDisabled: boolean;

  @Output() generateCharts = new EventEmitter<IFilterConfigurations>();

  localFilterConfig: IFilterConfigurations;

    constructor(private apiSrv: FilterApiService, private filterStateService: FilterStateService) { }

  ngOnInit(): void {
    this.localFilterConfig = {...this.filterConfigurations};
    
    const globalViewType = this.filterStateService.getSelectedViewType();
    const globalViewId = this.filterStateService.viewTypes.find(view => view.name === globalViewType)?.id;
    
    if (globalViewId !== undefined) {
      this.localFilterConfig.viewType.forEach(view => {
        view.checked = view.id === globalViewId;
      });
    }
  }

  changeDeviceType(id: string) {
    this.localFilterConfig.deviceTypes.forEach(device => {
      if (device.id === Number(id)) {
        device.checked = true;
      } else {
        device.checked = false;
      }
    })
  }

  async changeViewType(id: string) {
    this.localFilterConfig.viewType.forEach(view => {
      if (view.id === Number(id)) {
        view.checked = true;
      } else {
        view.checked = false;
      }
    });
    
    this.filterStateService.setGlobalViewType(Number(id));
    
    // Update PI list immediately when view type changes
    const selectedViewType = this.getSelectedViewType();
    await this.updateProgramIncrementsList(selectedViewType);
  }

  changePlatformType(id: string) {
    this.localFilterConfig.platformType.forEach(platform => {
      if (platform.id === Number(id)) {
        platform.checked = true;
      } else {
        platform.checked = false;
      }
    })
  }

  changeNetworkType(id: string) {
    this.localFilterConfig.networkTypes.forEach(network => {
      if (network.id === Number(id)) {
        network.checked = true;
      } else {
        network.checked = false;
      }
    })
  }

  changeMachineConfig(id: string) {
    this.localFilterConfig.machineConfigs.forEach(mConfig => {
      if (mConfig.id === Number(id)) {
        mConfig.checked = true;
      } else {
        mConfig.checked = false;
      }
    })
  }

  selectAll() {
    this.localFilterConfig.programIncrementsList.forEach(piData => piData.checked = true);
    this.localFilterConfig.testCasesList.forEach(tcData => tcData.checked = true);
  }

  clearAll() {
    this.localFilterConfig.programIncrementsList.forEach(piData => piData.checked = false);
    this.localFilterConfig.testCasesList.forEach(tcData => tcData.checked = false);
  }

  async generate() {
    // Get the selected view type before emitting
    const selectedViewType = this.getSelectedViewType();
    
    // Update programIncrementsList with fresh data based on current view type
    await this.updateProgramIncrementsList(selectedViewType);
    
    this.generateCharts.emit(this.localFilterConfig);
  }

  isGenerateAvailable() {
    return (this.localFilterConfig.programIncrementsList.findIndex(piData => piData.checked === true) !== -1) &&
    (this.localFilterConfig.testCasesList.findIndex(tcData => tcData.checked === true) !== -1) &&
    !this.filterButtonsDisabled;
  }

  private getSelectedViewType(): string {
    const selectedView = this.localFilterConfig.viewType?.find(view => view.checked);
    return selectedView ? selectedView.name : '';
  }

  private async updateProgramIncrementsList(viewType: string): Promise<void> {
    try {
      // Store current deselections to preserve user choices
      // New items will default to checked: true, but explicitly deselected items should remain false
      const currentDeselections = this.localFilterConfig.programIncrementsList
        .filter(pi => !pi.checked)
        .map(pi => pi.piName);

      // Fetch fresh PI data based on product type and view type
      let freshPIData: string[] = [];
      
      switch (this.productType) {
        case ProductType.ABT_PRO:
        case ProductType.ABT_SITE:
          freshPIData = await this.apiSrv.getProAndSitePIData(viewType).toPromise();
          break;
        case ProductType.ABT_GO:
          freshPIData = await this.apiSrv.getGoPIData(viewType).toPromise();
          break;
        case ProductType.ABT_FP:
          freshPIData = await this.apiSrv.getFPPIData(viewType).toPromise();
          break;
        case ProductType.ABT_CLOUD:
          freshPIData = await this.apiSrv.getCloudPIData(viewType).toPromise();
          break;
        case ProductType.ABT_DCP:
          freshPIData = await this.apiSrv.getDCPPIData(viewType).toPromise();
          break;
        case ProductType.TIA_V17:
          freshPIData = await this.apiSrv.getTIAV17Data(viewType).toPromise();
          break;
        case ProductType.ABT_ST:
          freshPIData = await this.apiSrv.getSystemTestData(viewType).toPromise();
          break;
        case ProductType.ABT_KNX:
          freshPIData = await this.apiSrv.getKNXData(viewType).toPromise();
          break;
        case ProductType.ABT_MBUS:
          freshPIData = await this.apiSrv.getMBusData(viewType).toPromise();
          break;
        default:
          console.warn('Unknown product type:', this.productType);
          return;
      }

      // Convert to extended format and preserve user selections
      // Default to checked: true for new items, preserve user choices for existing items
      this.localFilterConfig.programIncrementsList = freshPIData.map(piName => ({
        piName,
        checked: currentDeselections.includes(piName) ? false : true
      }));

    } catch (error) {
      console.error('Error updating program increments list:', error);
      // In case of error, keep the existing data
    }
  }

}
